"use client";

import { useState, useEffect, useMemo } from "react";
import { User } from "@/common/model/User";
import { UserSearchInput } from "../ui";
import { LBSubscriptionService, LBSubscriptionStatus } from "@/services/LBSubscriptionService";
import axiosInstance from "@/api/axiosInstance";
import { ENDPOINTS } from "@/constants/api";
import { Tabs, Tab } from "../ui/Tabs";
import {
    UserDetailsTab,
    ProDetailsTab,
    PrivacyDetailsTab,
    NotificationsTab,
    AuditLogTab,
    DeviceDetailsTab,
    ConnectionDetailsTab,
    LoginHistoryTab,
    StokesTab,
} from "./tabs";
import { SessionsTab } from "./tabs/sessions";
import { UserIcon, XMarkIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import RestoreUserModal from "./RestoreUserModal";

type UserManagerProps = {
    selectedUser: User | null;
    onSelectUser: (user: User | null) => void;
    activeTab?: string;
    onTabChange?: (tabId: string) => void;
    onSessionSelect?: (sessionId: number | null) => void;
    onUploadSelect?: (uploadId: string | null) => void;
};

const UserManager: React.FC<UserManagerProps> = ({
    selectedUser,
    onSelectUser,
    activeTab: externalActiveTab,
    onTabChange,
    onSessionSelect,
    onUploadSelect,
}) => {
    const [subscriptionStatus, setSubscriptionStatus] = useState<LBSubscriptionStatus | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Use external tab if provided, otherwise manage internally
    const [internalActiveTab, setInternalActiveTab] = useState<string>("user-details");
    const [showRestoreModal, setShowRestoreModal] = useState(false);

    const activeTabId = externalActiveTab ?? internalActiveTab;

    const handleTabChange = (tabId: string) => {
        if (onTabChange) {
            onTabChange(tabId);
        } else {
            setInternalActiveTab(tabId);
        }
    };

    useEffect(() => {
        if (selectedUser) {
            fetchSubscriptionStatus();
        } else {
            setSubscriptionStatus(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUser]);

    const fetchSubscriptionStatus = async () => {
        if (!selectedUser) return;

        setIsLoading(true);
        setError(null);
        try {
            const status = await LBSubscriptionService.getStatus(selectedUser.id);
            setSubscriptionStatus(status);
        } catch (err: any) {
            setError(err.message || "Failed to fetch user details");
            setSubscriptionStatus(null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleSubscription = async (subscribed: boolean, reason?: string) => {
        if (!selectedUser) return;

        setIsLoading(true);
        setError(null);
        try {
            const updatedStatus = await LBSubscriptionService.updateStatus(selectedUser.id, { subscribed, reason });
            // The LB-subscription PUT returns a PARTIAL user object — it doesn't touch the auth
            // fields at all. Merge onto the existing full details so those tabs keep their data.
            // Crucially, DO NOT let the partial response overwrite the auth fields: the list
            // fields come back omitted/null, but the `*Unavailable` booleans serialize as a
            // present `false` (Java primitive), which would clobber a prior `true` and flip a
            // "could not load" state into a misleading "no data". Always carry the auth fields
            // forward from `prev` unchanged (the toggle never legitimately updates them).
            setSubscriptionStatus((prev) => {
                if (!prev) return updatedStatus;
                return {
                    ...prev,
                    ...updatedStatus,
                    loginHistory: prev.loginHistory,
                    linkedIdentities: prev.linkedIdentities,
                    lastActiveAt: prev.lastActiveAt,
                    loginHistoryUnavailable: prev.loginHistoryUnavailable,
                    linkedIdentitiesUnavailable: prev.linkedIdentitiesUnavailable,
                };
            });
        } catch (err: any) {
            setError(err.message || "Failed to update subscription status");
            // Refresh status on error
            await fetchSubscriptionStatus();
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateEmail = async (newEmail: string) => {
        if (!selectedUser) return;

        setError(null);
        try {
            await axiosInstance.put(ENDPOINTS.ADMIN.UPDATE_EMAIL(selectedUser.id), {
                email: newEmail,
            });
            // Refresh the status to get the updated email
            await fetchSubscriptionStatus();
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || err.message || "Failed to update email";
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const tabs = useMemo<Tab[]>(() => {
        if (!selectedUser || !subscriptionStatus) return [];

        return [
            {
                id: "user-details",
                label: "User Details",
                content: (
                    <UserDetailsTab
                        user={selectedUser}
                        subscriptionStatus={subscriptionStatus}
                        onUpdateEmail={handleUpdateEmail}
                    />
                ),
            },
            {
                id: "pro-details",
                label: "PRO Details",
                content: (
                    <ProDetailsTab
                        subscriptionStatus={subscriptionStatus}
                        userId={selectedUser.id}
                        onRefresh={fetchSubscriptionStatus}
                    />
                ),
            },
            {
                id: "device-details",
                label: "Device Details",
                content: (
                    <DeviceDetailsTab
                        devices={subscriptionStatus.devices}
                        userDetails={subscriptionStatus}
                        onRefresh={fetchSubscriptionStatus}
                        onUploadSelect={onUploadSelect}
                    />
                ),
            },
            {
                id: "sessions",
                label: `Sessions${subscriptionStatus.sessionsCount ? ` (${subscriptionStatus.sessionsCount})` : ""}`,
                content: (
                    <SessionsTab
                        userId={selectedUser.id}
                        onSessionSelect={onSessionSelect}
                    />
                ),
            },
            {
                id: "connection-details",
                label: "Connection Details",
                content: <ConnectionDetailsTab userDetails={subscriptionStatus} />,
            },
            {
                id: "login-history",
                label: "Login History",
                content: (
                    <LoginHistoryTab
                        loginHistory={subscriptionStatus.loginHistory}
                        unavailable={subscriptionStatus.loginHistoryUnavailable}
                    />
                ),
            },
            {
                id: "privacy-details",
                label: "Privacy Details",
                content: <PrivacyDetailsTab subscriptionStatus={subscriptionStatus} />,
            },
            {
                id: "notifications-details",
                label: "Notifications Details",
                content: (
                    <NotificationsTab
                        subscriptionStatus={subscriptionStatus}
                        onToggle={handleToggleSubscription}
                        isLoading={isLoading}
                    />
                ),
            },
            {
                id: "audit-log",
                label: "Audit Log",
                content: <AuditLogTab auditLog={subscriptionStatus.auditLog} />,
            },
            {
                id: "stokes",
                label: "Stokes",
                content: <StokesTab userId={selectedUser.id} />,
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUser, subscriptionStatus, isLoading, onSessionSelect, onUploadSelect]);

    return (
        <>
        <div className="h-full flex flex-col">
            {/* Compact Header with Search */}
            <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center">
                        <UserIcon className="w-4 h-4 text-white" />
                    </div>
                    <h1 className="text-lg font-bold text-gray-900">Users</h1>
                </div>
                <div className="flex-1 max-w-md">
                    <UserSearchInput theme="light" onUserSelect={onSelectUser} adminMode={true} />
                </div>
                <button
                    onClick={() => setShowRestoreModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-sm font-medium transition-colors flex-shrink-0"
                    title="Restore a deleted user account"
                >
                    <ArrowPathIcon className="h-4 w-4" />
                    Restore User
                </button>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-4 py-2">
                    <div className="flex items-center gap-2">
                        <svg className="h-4 w-4 text-red-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                </div>
            )}

            {/* Main Content Area */}
            <div className="flex-1 min-h-0">
                {/* User Details Card */}
                {selectedUser && !isLoading && subscriptionStatus && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col overflow-hidden">
                        {/* Compact Header with Close Button */}
                        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-gray-900">
                                    {subscriptionStatus.name || selectedUser.username}
                                </span>
                                <span className="text-xs text-gray-500">{selectedUser.username}</span>
                                <span className="text-xs text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded font-mono">
                                    #{selectedUser.id}
                                </span>
                            </div>
                            <button
                                onClick={() => onSelectUser(null)}
                                className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors"
                                title="Close"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>

                        {/* Tabs Content */}
                        <div className="flex-1 overflow-auto p-4">
                            <Tabs tabs={tabs} activeTabId={activeTabId} onTabChange={handleTabChange} />
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {selectedUser && isLoading && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                        <div className="flex items-center justify-center gap-3">
                            <div className="relative">
                                <div className="w-8 h-8 rounded-full border-3 border-gray-200"></div>
                                <div className="absolute top-0 left-0 w-8 h-8 rounded-full border-3 border-cyan-500 border-t-transparent animate-spin"></div>
                            </div>
                            <p className="text-sm text-gray-600">Loading user details...</p>
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {!selectedUser && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                        <div className="flex items-center justify-center gap-4 text-center">
                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                <UserIcon className="w-6 h-6 text-gray-400" />
                            </div>
                            <div className="text-left">
                                <h3 className="text-sm font-medium text-gray-900">No user selected</h3>
                                <p className="text-xs text-gray-500">Search for a user to view their details</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>

        <RestoreUserModal
            isOpen={showRestoreModal}
            onClose={() => setShowRestoreModal(false)}
            onRestored={(userId) => {
                setShowRestoreModal(false);
            }}
        />
        </>
    );
};

export default UserManager;
